import axios from "axios";
import { devLog } from "./log";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Single shared axios instance — sends cookies on every request.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export async function fetchMe() {
  try {
    const res = await api.get("/auth/me");
    // Keep the favourites email in lockstep with the session so the
    // FavoriteHeart / MediaFavoriteHeart don't show the email-prompt
    // modal to already-signed-in users. Also cache the role so the
    // hearts can hide themselves for vendors (they shouldn't be
    // favouriting other vendors' work).
    try {
      if (res.data?.email) {
        localStorage.setItem("ld_couple_email", res.data.email.toLowerCase());
      }
      if (res.data?.role) {
        localStorage.setItem("ld_viewer_role", res.data.role);
      }
    } catch (e) {
      // Storage failures (private mode, quota, disabled) shouldn't crash the
      // session bootstrap — surface to console.debug for ops only.
      devLog("ld_fetchMe localStorage sync failed:", e?.message);
    }
    return res.data;
  } catch (e) {
    return null;
  }
}

export async function exchangeSession(session_id) {
  const res = await api.post("/auth/session", { session_id });
  return res.data;
}

export async function logout() {
  try {
    await api.post("/auth/logout");
  } catch (e) {
    // Network blip on logout is non-fatal — the session cookie has already
    // been cleared client-side.
    devLog("ld_logout API call failed:", e?.message);
  }
  // Clear the favourites email + cached role so the next account that
  // signs in on this device doesn't accidentally inherit the previous
  // user's favourites or visibility rules.
  try {
    localStorage.removeItem("ld_couple_email");
    localStorage.removeItem("ld_viewer_role");
  } catch (e) {
    devLog("ld_logout localStorage clear failed:", e?.message);
  }
}

/**
 * Gate for member-only routes (Vendor Listing, /my-favourites, category pages).
 * Returns { user, status, redirect }.
 *   - redirect === null     → user is fully onboarded, render the page
 *   - redirect === "/signin"        → not signed in
 *   - redirect === "/choose-role"   → signed in but role not picked
 *   - redirect === "/vendor/apply"  → vendor without a submitted application
 *   - redirect === "/couple/signup" → couple without a submitted application
 * Admin always passes through.
 *
 * Side-effect: persists the user's email to localStorage under
 * "ld_couple_email" so the favourites system (which is email-keyed) can
 * skip its email prompt for signed-in users.
 */
export async function requireMember() {
  const user = await fetchMe();
  if (!user) return { user: null, status: null, redirect: "/signin" };
  // Sync favourites email with session — keeps MediaFavoriteHeart /
  // FavoriteHeart from popping the EmailPromptModal once the user has
  // signed in.
  try {
    if (user.email) {
      localStorage.setItem("ld_couple_email", user.email.toLowerCase());
    }
  } catch (e) {
    devLog("ld_requireMember localStorage sync failed:", e?.message);
  }
  if (user.role === "admin") return { user, status: null, redirect: null };
  try {
    const { data: status } = await api.get("/auth/my-status");
    if (status.needs_role_pick) {
      return { user, status, redirect: "/choose-role" };
    }
    if (status.role === "vendor" && !status.has_vendor_application) {
      return { user, status, redirect: "/vendor/apply" };
    }
    if (status.role === "couple" && !status.has_couple_application) {
      return { user, status, redirect: "/couple/signup" };
    }
    return { user, status, redirect: null };
  } catch (e) {
    devLog("ld_requireMember my-status fetch failed:", e?.message);
    return { user, status: null, redirect: null };
  }
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function startGoogleSignIn(returnPath = "/admin") {
  const redirectUrl = window.location.origin + returnPath;
  window.location.href =
    "https://auth.emergentagent.com/?redirect=" +
    encodeURIComponent(redirectUrl);
}
